/**
 * Assignment drawer, in two flavours decided by `showProperties`:
 *
 *   off (default) — "Reassign Conversations". Ownership only, single column,
 *                   560px. This is the standalone Reassignment page's drawer
 *                   and is unchanged from before bulk properties existed.
 *   on            — "Bulk Action" (the inbox). Adds a Properties section and
 *                   widens to two columns, so a selection can be reclassified
 *                   and reassigned in one pass.
 *
 * Assignment fields cascade either way: Branch/Department need a Location,
 * Team is filtered by Campaign, Agent by Team.
 *
 * Agents already assigned to the selection are shown but disabled — reassigning
 * a conversation to whoever already has it is a no-op worth preventing. Each agent
 * also carries their current conversation load so a supervisor can see who is full
 * before adding to their pile.
 */
import { useState, useEffect, useMemo, type JSX, type ReactNode } from 'react'
import ActionDrawer from '../conversations/detail/ActionDrawer.js'
import {
  Field,
  SelectField,
  PrimaryButton,
  GhostButton,
  type Option,
} from '../conversations/detail/primitives.js'
import { Users, AlertTriangle, ArrowLeftRight, UserCog, SlidersHorizontal } from 'lucide-react'
import { useLocations } from '../../hooks/useLocations.js'
import { useBranches } from '../../hooks/useBranches.js'
import { useDepartments } from '../../hooks/useDepartments.js'
import { useReassignmentDropdowns, type DropdownOption } from '../../hooks/useReassignmentDropdowns.js'

/** Everything a bulk action can set. All optional — only what's set is applied. */
export interface BulkActionTarget {
  assignedTeamId?: string
  assignedAgentId?: string
  campaignId?: string
  locationId?: string
  branchId?: string
  departmentId?: string
  priorityId?: string
  /** The UI's "State" — conversations.status_id, matching the detail panel. */
  statusId?: string
  categoryId?: string
  subcategoryId?: string
  typeId?: string
  impactId?: string
  urgencyId?: string
  complexityId?: string
  /** Lifecycle status (conversations.status) — applied per conversation via the
   *  close / on-hold / reopen endpoints, after any assignment/property changes. */
  lifecycleStatus?: BulkLifecycleStatus
  /** Disposition, required when `lifecycleStatus` is 'closed'. */
  resolutionType?: BulkResolutionType
}

export type BulkLifecycleStatus = 'open' | 'on_hold' | 'closed'
export type BulkResolutionType = 'resolved' | 'merged' | 'cancelled'

const LIFECYCLE_OPTIONS: Option[] = [
  { value: 'open', label: 'Open' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' },
]

const RESOLUTION_OPTIONS: Option[] = [
  { value: 'resolved', label: 'Resolved' },
  { value: 'merged', label: 'Merged' },
  { value: 'cancelled', label: 'Cancelled' },
]

interface AssignDrawerProps {
  open: boolean
  onClose: () => void
  selectedCount: number
  teams: { id: string; name: string; campaignId?: string | null }[]
  agents: { agentId: string; label: string; teamIds: string[]; load?: number; maxLoad?: number; atCapacity?: boolean }[]
  campaigns: { id: string; name: string }[]
  /** Agents already assigned to the selected conversations — disabled in the
   *  Agent dropdown, and if somehow chosen, blocks submit with a warning. */
  currentAssigneeAgentIds?: string[]
  submitting: boolean
  /**
   * Show the Properties section and widen the drawer — the inbox's Bulk Action.
   * Off keeps the original reassign-only drawer used by the Reassignment page.
   */
  showProperties?: boolean
  /** Show the Transfer mode in the inbox bulk-action drawer. */
  canBulkTransfer?: boolean
  canBulkAssignment?: boolean
  /** Show the lifecycle Status field (needs conversations:close). */
  canChangeStatus?: boolean
  onSubmit: (target: BulkActionTarget) => void
  onSubmitTransfer?: (target: { toTeamId: string; toAgentId?: string; description?: string }) => void
}

const toOptions = (rows: DropdownOption[]): Option[] =>
  rows.map((r) => ({ value: r.id, label: r.name }))

/** Section heading with a rule, so the two halves of the form read apart. */
function SectionHeading({ icon, title, hint }: { icon: ReactNode; title: string; hint: string }): JSX.Element {
  return (
    <div className="mb-3 border-b border-gray-100 pb-2">
      <h3 className="flex items-center gap-2 text-[13px] font-bold text-gray-900">
        <span className="text-gray-400">{icon}</span>
        {title}
      </h3>
      <p className="mt-0.5 text-[11.5px] text-gray-400">{hint}</p>
    </div>
  )
}

export default function ReassignDrawer({
  open,
  onClose,
  selectedCount,
  teams,
  agents,
  campaigns,
  currentAssigneeAgentIds = [],
  submitting,
  showProperties = false,
  canBulkTransfer = false,
  canBulkAssignment = true,
  canChangeStatus = false,
  onSubmit,
  onSubmitTransfer,
}: AssignDrawerProps): JSX.Element | null {
  const [actionMode, setActionMode] = useState<'assignment' | 'transfer'>('assignment')
  // Assignment
  const [locationId, setLocationId] = useState('')
  const [branchId, setBranchId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [campaignId, setCampaignId] = useState('')
  const [teamId, setTeamId] = useState('')
  const [agentId, setAgentId] = useState('')
  // Properties
  const [priorityId, setPriorityId] = useState('')
  const [statusId, setStatusId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [subcategoryId, setSubcategoryId] = useState('')
  const [typeId, setTypeId] = useState('')
  const [impactId, setImpactId] = useState('')
  const [urgencyId, setUrgencyId] = useState('')
  const [complexityId, setComplexityId] = useState('')
  const [lifecycleStatus, setLifecycleStatus] = useState('')
  const [resolutionType, setResolutionType] = useState<string>('resolved')
  const [transferNote, setTransferNote] = useState('')
  const [subcategories, setSubcategories] = useState<DropdownOption[]>([])

  const { locations } = useLocations()
  const { branches } = useBranches(locationId || undefined)
  const { departments } = useDepartments(branchId || undefined)
  const { data: catalog, fetchSubcategories } = useReassignmentDropdowns()

  // Reset form when drawer closes
  useEffect(() => {
    if (!open) {
      setLocationId(''); setBranchId(''); setDepartmentId('')
      setCampaignId(''); setTeamId(''); setAgentId('')
      setPriorityId(''); setStatusId(''); setCategoryId(''); setSubcategoryId('')
      setTypeId(''); setImpactId(''); setUrgencyId(''); setComplexityId('')
      setLifecycleStatus(''); setResolutionType('resolved')
      setActionMode('assignment'); setTransferNote('')
      setSubcategories([])
    }
  }, [open])

  // Reset downstream fields when parent changes
  useEffect(() => { setBranchId(''); setDepartmentId('') }, [locationId])
  useEffect(() => { setDepartmentId('') }, [branchId])
  useEffect(() => { setTeamId(''); setAgentId('') }, [campaignId])
  useEffect(() => { setAgentId('') }, [teamId])

  // Subcategories are per-category, so they're fetched rather than pre-loaded.
  useEffect(() => {
    setSubcategoryId('')
    if (!categoryId) { setSubcategories([]); return }
    let cancelled = false
    void fetchSubcategories(categoryId).then((rows) => { if (!cancelled) setSubcategories(rows) })
    return () => { cancelled = true }
  }, [categoryId, fetchSubcategories])

  const locationOptions: Option[] = useMemo(
    () => locations.map((l) => ({ value: l.id, label: l.name })), [locations],
  )
  const branchOptions: Option[] = useMemo(
    () => branches.map((b) => ({ value: b.id, label: b.name })), [branches],
  )
  const departmentOptions: Option[] = useMemo(
    () => departments.map((d) => ({ value: d.id, label: d.name })), [departments],
  )
  const campaignOptions: Option[] = useMemo(
    () => campaigns.map((c) => ({ value: c.id, label: c.name })), [campaigns],
  )
  const teamOptions: Option[] = useMemo(
    () => teams
      .filter((t) => actionMode === 'transfer' || !campaignId || String(t.campaignId ?? '') === campaignId)
      .map((t) => ({ value: t.id, label: t.name })),
    [teams, campaignId, actionMode],
  )
  const assigneeSet = useMemo(() => new Set(currentAssigneeAgentIds), [currentAssigneeAgentIds])

  const agentOptions: Option[] = useMemo(
    () => agents
      .filter((a) => !teamId || a.teamIds.includes(teamId))
      .map((a) => {
        // Show the load next to every agent so "who has room?" is answerable
        // here rather than only after the API refuses.
        const load = a.maxLoad && a.maxLoad > 0 ? ` · ${a.load ?? 0}/${a.maxLoad}` : ''
        if (assigneeSet.has(a.agentId)) {
          // Already assigned to the selected conversation(s): show but disable
          // so the agent can't be picked for a no-op reassign.
          return { value: a.agentId, label: `${a.label} (already assigned)`, disabled: true }
        }
        return { value: a.agentId, label: `${a.label}${load}${a.atCapacity ? ' — full' : ''}` }
      }),
    [agents, teamId, assigneeSet],
  )

  // Guard: even though the option is disabled, block submit if the chosen agent
  // is already assigned — the conversation is already with them.
  const agentAlreadyAssigned = agentId !== '' && assigneeSet.has(agentId)

  const assignmentFields = [teamId, agentId, campaignId, locationId, branchId, departmentId]
  const showStatus = showProperties && canChangeStatus
  const propertyFields = showProperties
    ? [priorityId, statusId, categoryId, subcategoryId, typeId, impactId, urgencyId, complexityId, showStatus ? lifecycleStatus : '']
    : []
  const changeCount = [...assignmentFields, ...propertyFields].filter(Boolean).length
  const transferMode = showProperties && canBulkTransfer && onSubmitTransfer !== undefined
    && (actionMode === 'transfer' || !canBulkAssignment)
  const hasTarget = transferMode ? Boolean(teamId) : changeCount > 0

  const handleSubmit = (): void => {
    if (!hasTarget || agentAlreadyAssigned) return
    if (transferMode) {
      onSubmitTransfer({
        toTeamId: teamId,
        ...(agentId ? { toAgentId: agentId } : {}),
        ...(transferNote.trim() ? { description: transferNote.trim() } : {}),
      })
      return
    }
    onSubmit({
      ...(teamId ? { assignedTeamId: teamId } : {}),
      ...(agentId ? { assignedAgentId: agentId } : {}),
      ...(campaignId ? { campaignId } : {}),
      ...(locationId ? { locationId } : {}),
      ...(branchId ? { branchId } : {}),
      ...(departmentId ? { departmentId } : {}),
      // Properties only exist in Bulk Action mode; never send stale state from
      // a hidden section.
      ...(showProperties
        ? {
            ...(priorityId ? { priorityId } : {}),
            ...(statusId ? { statusId } : {}),
            ...(categoryId ? { categoryId } : {}),
            ...(subcategoryId ? { subcategoryId } : {}),
            ...(typeId ? { typeId } : {}),
            ...(impactId ? { impactId } : {}),
            ...(urgencyId ? { urgencyId } : {}),
            ...(complexityId ? { complexityId } : {}),
            ...(showStatus && lifecycleStatus
              ? {
                  lifecycleStatus: lifecycleStatus as BulkLifecycleStatus,
                  ...(lifecycleStatus === 'closed' ? { resolutionType: (resolutionType || 'resolved') as BulkResolutionType } : {}),
                }
              : {}),
          }
        : {}),
    })
  }

  // Single-column, original width for the plain reassign drawer.
  const grid = showProperties ? 'grid gap-x-4 gap-y-3 sm:grid-cols-2' : 'space-y-4'

  return (
    <ActionDrawer
      open={open}
      onClose={onClose}
      title={showProperties ? 'Bulk Action' : 'Reassign Conversations'}
      subtitle={`${selectedCount} conversation${selectedCount !== 1 ? 's' : ''} selected`}
      icon={<Users className="h-4 w-4" />}
      info={{
        lead: transferMode
          ? 'Transfers every selected conversation and records each handover in its Transfers history.'
          : showProperties
          ? 'Reassigns and reclassifies every selected conversation in one pass.'
          : 'Moves every selected conversation to a new owner.',
        points: [
          { term: 'Cascade', detail: 'Branch and Department need a Location first; Team is filtered by Campaign, and Agent by Team.' },
          { term: 'Disabled agents', detail: 'Agents already holding one of the selected conversations are shown but cannot be picked — that would be a no-op.' },
          { term: 'Agent load', detail: 'Each agent lists their current open count, so you can see who is already full.' },
          ...(showProperties
            ? [{ term: 'Properties', detail: 'Only the fields you set are written; anything left blank is untouched on every conversation.' }]
            : []),
          ...(showStatus
            ? [{ term: 'Status', detail: 'Open, On Hold and Closed go through the normal reopen / hold / close flow for each conversation. A parent closes only once its child tickets are closed — select both and the children are closed first.' }]
            : []),
          ...(transferMode
            ? [{ term: 'Transfer history', detail: 'Each conversation is transferred through the normal transfer workflow, so the record appears in its Transfers tab.' }]
            : []),
        ],
        caution: `This applies to all ${selectedCount} selected conversation${selectedCount !== 1 ? 's' : ''} at once.`,
      }}
      width={showProperties ? 760 : 560}
      footer={
        <>
          {showProperties && (
            <span className="mr-auto text-[12px] text-gray-400">
              {transferMode
                ? hasTarget ? `Transfer ${selectedCount} conversation${selectedCount !== 1 ? 's' : ''} to this team` : 'Select a destination team'
                : hasTarget
                ? `${changeCount} change${changeCount !== 1 ? 's' : ''} will be applied to all ${selectedCount}`
                : 'Set at least one field'}
            </span>
          )}
          <GhostButton onClick={onClose} disabled={submitting}>
            Cancel
          </GhostButton>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={!hasTarget || agentAlreadyAssigned || submitting || selectedCount === 0}
          >
            {transferMode
              ? submitting ? 'Transferring…' : 'Transfer'
              : showProperties
              ? submitting ? 'Applying…' : 'Apply'
              : submitting ? 'Assigning…' : 'Assign'}
          </PrimaryButton>
        </>
      }
    >
      <div className={showProperties ? 'space-y-6' : ''}>
        {showProperties && canBulkTransfer && canBulkAssignment && onSubmitTransfer && (
          <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-1" role="tablist" aria-label="Bulk action type">
            <button type="button" role="tab" aria-selected={!transferMode} onClick={() => setActionMode('assignment')}
              className={`inline-flex items-center gap-2 rounded px-3 py-2 text-xs font-semibold transition-colors ${!transferMode ? 'bg-white text-[#3366FF] shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
              <UserCog className="h-3.5 w-3.5" />Assignment
            </button>
            <button type="button" role="tab" aria-selected={transferMode} onClick={() => setActionMode('transfer')}
              className={`inline-flex items-center gap-2 rounded px-3 py-2 text-xs font-semibold transition-colors ${transferMode ? 'bg-white text-[#3366FF] shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
              <ArrowLeftRight className="h-3.5 w-3.5" />Transfer
            </button>
          </div>
        )}
        {/* ── Assignment ─────────────────────────────────────────────── */}
        <section>
          {showProperties && (
            <SectionHeading
              icon={transferMode ? <ArrowLeftRight className="h-4 w-4" /> : <UserCog className="h-4 w-4" />}
              title={transferMode ? 'Transfer destination' : 'Assignment'}
              hint={transferMode ? 'Choose the receiving team and optionally a specific agent.' : 'Who owns these conversations and where they sit in the org.'}
            />
          )}
          <div className={grid}>
            {!transferMode && <Field label="Location">
              <SelectField
                value={locationId}
                onChange={setLocationId}
                placeholder="Select location"
                options={locationOptions}
                allowClear
              />
            </Field>}

            {!transferMode && <Field label="Branch">
              <SelectField
                value={branchId}
                onChange={setBranchId}
                placeholder={locationId ? 'Select branch' : 'Select a location first'}
                options={branchOptions}
                disabled={!locationId}
                allowClear
              />
            </Field>}

            {!transferMode && <Field label="Department">
              <SelectField
                value={departmentId}
                onChange={setDepartmentId}
                placeholder={locationId ? 'Select department' : 'Select a location first'}
                options={departmentOptions}
                disabled={!locationId}
                allowClear
              />
            </Field>}

            {!transferMode && <Field label="Campaign">
              <SelectField
                value={campaignId}
                onChange={setCampaignId}
                placeholder="Select campaign"
                options={campaignOptions}
                allowClear
              />
            </Field>}

            <Field label="Team" required={transferMode}>
              <SelectField
                value={teamId}
                onChange={setTeamId}
                placeholder={transferMode ? 'Select destination team' : campaignId ? 'Select team' : 'Select a campaign first'}
                options={teamOptions}
                disabled={!transferMode && !campaignId}
                allowClear
              />
            </Field>

            <Field
              label="Agent"
              hint={transferMode ? 'Optional. Leave empty to transfer to the team queue.' : 'Leave empty to push these to the omnichannel queue for auto assignment.'}
            >
              <SelectField
                value={agentId}
                onChange={setAgentId}
                placeholder={teamId ? 'Select agent' : 'Select a team first'}
                options={agentOptions}
                disabled={!teamId}
                allowClear
              />
              {agentAlreadyAssigned && (
                <p className="mt-1.5 flex items-start gap-1.5 text-[12px] font-medium text-amber-700">
                  <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                  This agent is already assigned to the selected conversation. Pick a different agent.
                </p>
              )}
            </Field>
            {transferMode && (
              <Field label="Transfer note" hint="Optional. Added to each conversation’s transfer history.">
                <textarea
                  value={transferNote}
                  onChange={(event) => setTransferNote(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Handover details for the receiving team"
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-[#3366FF] focus:ring-2 focus:ring-[#3366FF]/15"
                />
              </Field>
            )}
          </div>
        </section>

        {/* ── Properties (Bulk Action only) ──────────────────────────── */}
        {showProperties && !transferMode && (
        <section>
          <SectionHeading
            icon={<SlidersHorizontal className="h-4 w-4" />}
            title="Properties"
            hint="Applied to every selected conversation. Anything left blank is left alone."
          />
          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
            {showStatus && (
              <Field label="Status" hint="Open, On Hold or Closed — applied to every selected conversation.">
                <SelectField
                  value={lifecycleStatus}
                  onChange={setLifecycleStatus}
                  placeholder="Select status"
                  options={LIFECYCLE_OPTIONS}
                  allowClear
                />
              </Field>
            )}

            {showStatus && lifecycleStatus === 'closed' && (
              <Field label="Close disposition" required>
                <SelectField
                  value={resolutionType}
                  onChange={setResolutionType}
                  placeholder="Select disposition"
                  options={RESOLUTION_OPTIONS}
                />
              </Field>
            )}

            <Field label="Priority">
              <SelectField
                value={priorityId}
                onChange={setPriorityId}
                placeholder="Select priority"
                options={toOptions(catalog.priorities)}
                allowClear
              />
            </Field>

            <Field label="State">
              <SelectField
                value={statusId}
                onChange={setStatusId}
                placeholder="Select state"
                options={toOptions(catalog.states)}
                allowClear
              />
            </Field>

            <Field label="Category">
              <SelectField
                value={categoryId}
                onChange={setCategoryId}
                placeholder="Select category"
                options={toOptions(catalog.categories)}
                allowClear
              />
            </Field>

            <Field label="Subcategory">
              <SelectField
                value={subcategoryId}
                onChange={setSubcategoryId}
                placeholder={categoryId ? 'Select subcategory' : 'Select a category first'}
                options={toOptions(subcategories)}
                disabled={!categoryId}
                allowClear
              />
            </Field>

            <Field label="Type">
              <SelectField
                value={typeId}
                onChange={setTypeId}
                placeholder="Select type"
                options={toOptions(catalog.types)}
                allowClear
              />
            </Field>

            <Field label="Impact">
              <SelectField
                value={impactId}
                onChange={setImpactId}
                placeholder="Select impact"
                options={toOptions(catalog.impacts)}
                allowClear
              />
            </Field>

            <Field label="Urgency">
              <SelectField
                value={urgencyId}
                onChange={setUrgencyId}
                placeholder="Select urgency"
                options={toOptions(catalog.urgencies)}
                allowClear
              />
            </Field>

            <Field label="Complexity">
              <SelectField
                value={complexityId}
                onChange={setComplexityId}
                placeholder="Select complexity"
                options={toOptions(catalog.complexities)}
                allowClear
              />
            </Field>
          </div>
        </section>
        )}
      </div>
    </ActionDrawer>
  )
}
